import {
  type OutputFieldCondition,
  SearchField,
  SearchMatchType,
  SearchSortField,
} from '@compass/client/types/case_record';
import { CaseState, KnipperCaseState, PqcDecision } from '@constants/caseStates';
import { WorkflowType } from '@constants/dispositions';

import type { SortState } from '@/types/table';

export interface QuickFilterConfig {
  id: string;
  name: string;
  badgeColor?: string; // Tailwind color class for the badge
  filter: {
    states?: string[];
    /**
     * PQC submitted-substate tabs (Ready to Complete / Follow-up Required /
     * Submitted) filter `submitted` cases by output_data fields. When present,
     * the tab is served by the Atlas advanced-search path (SearchCaseRecords)
     * instead of the standard FindCaseRecords path, with these conditions
     * AND-combined into the compound $search. Empty/absent → standard path.
     */
    outputConditions?: OutputFieldCondition[];
  };
  /**
   * Optional sort to apply when the user navigates to this tab (initial landing
   * or click). Used by PQC to give Pending `created_at` asc while other tabs
   * snap back to desc. Tabs without it leave sort state untouched on click.
   */
  defaultSort?: SortState[];
  /**
   * Hide the date picker on this tab and pin its list/count queries to the
   * last 12 months (`getPqcPinnedDateRange`, AQ-863), independent of the
   * picker's own default (today). Work queues don't need a user-chosen range
   * but must stay bounded, since an undated query timed out on prod.
   */
  hidesDatePicker?: boolean;
  /**
   * Default sort for advanced-search (output-condition) tabs. Maps to a
   * SearchSortField on the advanced path; `advancedSortAsc` picks the direction
   * (default ascending — oldest first, per the lifecycle-tab spec).
   */
  advancedSort?: SearchSortField;
  advancedSortAsc?: boolean;
}

// PQC manufacturer-reply decisions that mean an outstanding follow-up (vs an ack).
const PQC_FOLLOW_UP_DECISIONS = [PqcDecision.QUESTION_ASKED, PqcDecision.MISSING_INFO];

// Reusable output conditions for the submitted-substate tabs (Follow-up wins the
// ack_id + follow-up overlap, per the prototype).
const ACK_ID_EXISTS: OutputFieldCondition = {
  field: SearchField.SEARCH_FIELD_ACK_ID,
  match_type: SearchMatchType.SEARCH_MATCH_TYPE_EXISTS,
};
const ACK_ID_NOT_EXISTS: OutputFieldCondition = {
  field: SearchField.SEARCH_FIELD_ACK_ID,
  match_type: SearchMatchType.SEARCH_MATCH_TYPE_NOT_EXISTS,
};
const DECISION_IS_FOLLOW_UP: OutputFieldCondition = {
  field: SearchField.SEARCH_FIELD_PQC_DECISION,
  match_type: SearchMatchType.SEARCH_MATCH_TYPE_EXACT,
  values: PQC_FOLLOW_UP_DECISIONS,
};
const DECISION_NOT_FOLLOW_UP: OutputFieldCondition = {
  field: SearchField.SEARCH_FIELD_PQC_DECISION,
  match_type: SearchMatchType.SEARCH_MATCH_TYPE_NOT_EXACT,
  values: PQC_FOLLOW_UP_DECISIONS,
};

export interface FiltersConfig {
  filters: QuickFilterConfig[];
}

// Phase-specific states for medicare_human and other multi-phase workflows
const HUMAN_CALL_STATES = [CaseState.HUMAN_CALL, 'primary_human_call', 'secondary_human_call'];

const HUMAN_AUDIT_STATES = [CaseState.HUMAN_AUDIT, 'primary_human_audit', 'secondary_human_audit'];

const quickFiltersConfig = {
  filters: [
    {
      id: 'all',
      name: 'All',
      badgeColor: 'bg-purple-100 text-purple-700',
      filter: {},
    },
    {
      id: 'ai-call',
      name: 'AI Call',
      badgeColor: 'bg-blue-100 text-blue-700',
      filter: {
        states: [CaseState.AI_AUDIT, CaseState.AI_CALL],
      },
    },
    {
      id: 'human-call',
      name: 'Human Call',
      badgeColor: 'bg-orange-50 text-orange-700',
      filter: {
        states: HUMAN_CALL_STATES,
      },
    },
    {
      id: 'human-audit',
      name: 'Human Audit',
      badgeColor: 'bg-yellow-100 text-yellow-700',
      filter: {
        states: HUMAN_AUDIT_STATES,
      },
    },
    {
      id: 'terminated',
      name: 'Terminated',
      badgeColor: 'bg-red-50 text-red-700',
      filter: {
        states: [CaseState.TERMINATED],
      },
    },
    {
      id: 'completed',
      name: 'Completed',
      badgeColor: 'bg-green-100 text-green-700',
      filter: {
        states: [CaseState.COMPLETED],
      },
    },
  ] as QuickFilterConfig[],
};

const pharmBVFiltersConfig = {
  filters: [
    {
      id: 'all',
      name: 'All',
      badgeColor: 'bg-purple-100 text-purple-700',
      filter: {},
    },
    {
      id: 'ai-call',
      name: 'AI Call',
      badgeColor: 'bg-blue-100 text-blue-700',
      filter: {
        states: [CaseState.AI_AUDIT, CaseState.AI_CALL],
      },
    },
    {
      id: 'human-call',
      name: 'Human Call',
      badgeColor: 'bg-orange-50 text-orange-700',
      filter: {
        states: HUMAN_CALL_STATES,
      },
    },
    {
      id: 'human-audit',
      name: 'Human Audit',
      badgeColor: 'bg-yellow-100 text-yellow-700',
      filter: {
        states: HUMAN_AUDIT_STATES,
      },
    },
    {
      id: 'missing-info',
      name: 'Missing Info',
      badgeColor: 'bg-amber-50 text-amber-700',
      filter: {
        states: [CaseState.HUMAN_STEP_MISSING_INFO],
      },
    },
    {
      id: 'terminated',
      name: 'Terminated',
      badgeColor: 'bg-red-50 text-red-700',
      filter: {
        states: [CaseState.TERMINATED],
      },
    },
    {
      id: 'completed',
      name: 'Completed',
      badgeColor: 'bg-green-100 text-green-700',
      filter: {
        states: [CaseState.COMPLETED],
      },
    },
  ] as QuickFilterConfig[],
};

const medBVFiltersConfig = {
  filters: [
    {
      id: 'all',
      name: 'All',
      badgeColor: 'bg-purple-100 text-purple-700',
      filter: {},
    },
    {
      id: 'ai-call',
      name: 'AI Call',
      badgeColor: 'bg-blue-100 text-blue-700',
      filter: {
        states: [CaseState.AI_AUDIT, CaseState.AI_CALL],
      },
    },
    {
      id: 'human-call',
      name: 'Human Call',
      badgeColor: 'bg-orange-50 text-orange-700',
      filter: {
        states: HUMAN_CALL_STATES,
      },
    },
    {
      id: 'human-audit',
      name: 'Human Audit',
      badgeColor: 'bg-yellow-100 text-yellow-700',
      filter: {
        states: HUMAN_AUDIT_STATES,
      },
    },
    {
      id: 'missing-info',
      name: 'Missing Info',
      badgeColor: 'bg-amber-50 text-amber-700',
      filter: {
        states: [CaseState.HUMAN_STEP_MISSING_INFO],
      },
    },
    {
      id: 'eligibility-check',
      name: 'Eligibility Check',
      badgeColor: 'bg-purple-50 text-purple-700',
      filter: {
        states: [
          CaseState.PRIMARY_ELIGIBILITY_CHECK,
          CaseState.PRIMARY_ELIGIBILITY_CHECK_HUMAN_AUDIT,
          CaseState.SECONDARY_ELIGIBILITY_CHECK,
          CaseState.SECONDARY_ELIGIBILITY_CHECK_HUMAN_AUDIT,
          CaseState.ELIGIBILITY_CHECK_HUMAN_AUDIT,
        ],
      },
    },
    {
      id: 'terminated',
      name: 'Terminated',
      badgeColor: 'bg-red-50 text-red-700',
      filter: {
        states: [CaseState.TERMINATED],
      },
    },
    {
      id: 'completed',
      name: 'Completed',
      badgeColor: 'bg-green-100 text-green-700',
      filter: {
        states: [CaseState.COMPLETED],
      },
    },
  ] as QuickFilterConfig[],
};

const unifiedMedBVFiltersConfig: FiltersConfig = {
  filters: [
    {
      id: 'all',
      name: 'All',
      badgeColor: 'bg-purple-100 text-purple-700',
      filter: {},
    },
    {
      id: 'ai-call',
      name: 'AI Call',
      badgeColor: 'bg-blue-100 text-blue-700',
      filter: {
        states: [CaseState.AI_CALL, CaseState.AI_AUDIT, CaseState.AI_CALL_AUDIT],
      },
    },
    {
      id: 'human-call',
      name: 'Human Call',
      badgeColor: 'bg-orange-50 text-orange-700',
      filter: {
        states: HUMAN_CALL_STATES,
      },
    },
    {
      id: 'human-audit',
      name: 'Human Audit',
      badgeColor: 'bg-yellow-100 text-yellow-700',
      filter: {
        states: HUMAN_AUDIT_STATES,
      },
    },
    {
      id: 'missing-info',
      name: 'Missing Info',
      badgeColor: 'bg-amber-50 text-amber-700',
      filter: {
        states: [CaseState.HUMAN_STEP_MISSING_INFO],
      },
    },
    {
      id: 'eligibility-check',
      name: 'Eligibility Check',
      badgeColor: 'bg-purple-50 text-purple-700',
      filter: {
        states: [CaseState.ELIGIBILITY_CHECK, CaseState.EC_AI_AUDIT, CaseState.EC_HUMAN_AUDIT],
      },
    },
    {
      id: 'terminated',
      name: 'Terminated',
      badgeColor: 'bg-red-50 text-red-700',
      filter: {
        states: [CaseState.TERMINATED, CaseState.CANCELLED],
      },
    },
    {
      id: 'completed',
      name: 'Completed',
      badgeColor: 'bg-green-100 text-green-700',
      filter: {
        states: [CaseState.COMPLETED],
      },
    },
  ],
};

const recallSchedulerFiltersConfig: FiltersConfig = {
  filters: [
    {
      id: 'all',
      name: 'All',
      badgeColor: 'bg-purple-100 text-purple-700',
      filter: {},
    },
    {
      id: 'ai-call-scheduled',
      name: 'AI Call Scheduled',
      badgeColor: 'bg-blue-100 text-blue-700',
      filter: {
        states: [CaseState.AI_CALL_SCHEDULED],
      },
    },
    {
      id: 'ai-call',
      name: 'AI Call',
      badgeColor: 'bg-blue-100 text-blue-700',
      filter: {
        states: [CaseState.AI_AUDIT, CaseState.AI_CALL],
      },
    },
    {
      id: 'call-review',
      name: 'Call Review',
      badgeColor: 'bg-amber-50 text-amber-700',
      filter: {
        states: [CaseState.CALL_REVIEW],
      },
    },
    {
      id: 'human-call',
      name: 'Human Call',
      badgeColor: 'bg-orange-50 text-orange-700',
      filter: {
        states: HUMAN_CALL_STATES,
      },
    },
    {
      id: 'human-audit',
      name: 'Human Audit',
      badgeColor: 'bg-yellow-100 text-yellow-700',
      filter: {
        states: HUMAN_AUDIT_STATES,
      },
    },
    {
      id: 'reach-out-ineligible',
      name: 'Reach-out Ineligible',
      badgeColor: 'bg-yellow-100 text-yellow-700',
      filter: {
        states: [CaseState.REACHOUT_INELIGIBLE],
      },
    },
    {
      id: 'terminated',
      name: 'Terminated',
      badgeColor: 'bg-red-50 text-red-700',
      filter: {
        states: [CaseState.TERMINATED],
      },
    },
    {
      id: 'completed',
      name: 'Completed',
      badgeColor: 'bg-green-100 text-green-700',
      filter: {
        states: [CaseState.COMPLETED],
      },
    },
  ],
};

// Knipper/PQC states explicitly surfaced by their own tabs. The Pending tab combines
// the two user-actionable states (INTAKE_DRAFT + REVIEW_NEEDED); the Cancelled tab
// combines NON_REPORTABLE + CANCELLED; the Other tab is computed as the remainder.
const KNIPPER_PENDING_STATES = [KnipperCaseState.INTAKE_DRAFT, KnipperCaseState.REVIEW_NEEDED];
const KNIPPER_CANCELLED_STATES = [KnipperCaseState.NON_REPORTABLE, KnipperCaseState.CANCELLED];
const KNIPPER_TABBED_STATES = new Set<string>([
  ...KNIPPER_PENDING_STATES,
  ...KNIPPER_CANCELLED_STATES,
  KnipperCaseState.SUBMITTED,
  KnipperCaseState.ACKNOWLEDGED,
  KnipperCaseState.DUPLICATE,
]);
// `Start` (capital S) is the only Knipper enum value that violates the server's
// status regex (lowercase letters/digits/underscores/hyphens only) and would 400
// the counts API. Filter to API-compliant states only.
const KNIPPER_OTHER_STATES = Object.values(KnipperCaseState).filter(
  s => !KNIPPER_TABBED_STATES.has(s) && /^[a-z0-9_-]+$/.test(s),
);

export const KNIPPER_PENDING_FILTER_ID = 'pending';

// Sort snaps on tab entry. Work-queue tabs (Pending / Submitted / Follow-up /
// Ready / Other) surface the longest-pending case first — oldest created_at
// ascending (Follow-up / Ready / Submitted override with their own lifecycle
// timestamp via advancedSort). Historical/terminal tabs browse newest-first.
const WORK_QUEUE_DEFAULT_SORT: SortState[] = [{ field: 'created_at', direction: 'asc' }];
const HISTORICAL_DEFAULT_SORT: SortState[] = [{ field: 'created_at', direction: 'desc' }];

// Standard (pre-Tier-2) PQC quick-filter tabs. This is the DEFAULT config
// returned by getFiltersConfigForWorkflowType(PQC) and used by the per-program
// CasesContainer. The Org-wide Cases (org-wide lifecycle tabs) lives in
// knipperPqcLifecycleFiltersConfig below and is consumed ONLY by
// Tier2CasesContainer — so the entire lifecycle-tab behavior is gated behind the
// feature flag (flag off → these standard tabs everywhere).
const knipperFiltersConfig: FiltersConfig = {
  filters: [
    {
      id: KNIPPER_PENDING_FILTER_ID,
      name: 'Pending',
      badgeColor: 'bg-amber-100 text-amber-700',
      filter: {
        states: KNIPPER_PENDING_STATES,
      },
      defaultSort: WORK_QUEUE_DEFAULT_SORT,
      hidesDatePicker: true,
    },
    {
      id: 'submitted',
      name: 'Submitted',
      badgeColor: 'bg-blue-100 text-blue-700',
      filter: {
        states: [KnipperCaseState.SUBMITTED],
      },
      // Shows the date picker (LA-8170); still oldest submitted first (AQ-863).
      defaultSort: WORK_QUEUE_DEFAULT_SORT,
    },
    {
      id: 'completed',
      name: 'Completed',
      badgeColor: 'bg-green-100 text-green-700',
      filter: {
        // ACKNOWLEDGED = manufacturer responded, no more agent action. Warm-transfer cases
        // (record-only) also land here — they transition Start → Acknowledged (no submission),
        // so they surface under Completed without needing the terminal COMPLETED state here.
        states: [KnipperCaseState.ACKNOWLEDGED],
      },
      defaultSort: HISTORICAL_DEFAULT_SORT,
    },
    {
      id: 'cancelled',
      name: 'Cancelled',
      badgeColor: 'bg-red-50 text-red-700',
      filter: {
        states: KNIPPER_CANCELLED_STATES,
      },
      defaultSort: HISTORICAL_DEFAULT_SORT,
    },
    {
      id: 'duplicate',
      name: 'Duplicate',
      badgeColor: 'bg-yellow-100 text-yellow-700',
      filter: {
        states: [KnipperCaseState.DUPLICATE],
      },
      defaultSort: HISTORICAL_DEFAULT_SORT,
    },
    {
      id: 'other',
      name: 'Other',
      badgeColor: 'bg-slate-100 text-slate-700',
      filter: {
        states: KNIPPER_OTHER_STATES,
      },
      defaultSort: HISTORICAL_DEFAULT_SORT,
    },
    {
      id: 'all',
      name: 'All',
      badgeColor: 'bg-purple-100 text-purple-700',
      filter: {},
      defaultSort: HISTORICAL_DEFAULT_SORT,
    },
  ],
};

// Org-wide Cases (Tier-2, AQ-180): org-wide PQC lifecycle tabs — Submitted
// split into Follow-up Required / Submitted / Ready to Complete and work-queue
// sorts. The work-queue tabs (Pending / Follow-up / Submitted / Ready / Other)
// hide the date picker and are pinned to the last 12 months (AQ-863);
// the historical tabs honor the picker. Consumed ONLY by
// Tier2CasesContainer (behind
// the orgWideCasesEnabled flag); the standard knipperFiltersConfig above is
// what every non-flag path sees.
export const knipperPqcLifecycleFiltersConfig: FiltersConfig = {
  filters: [
    {
      id: KNIPPER_PENDING_FILTER_ID,
      name: 'Pending',
      badgeColor: 'bg-amber-100 text-amber-700',
      filter: {
        states: KNIPPER_PENDING_STATES,
      },
      defaultSort: WORK_QUEUE_DEFAULT_SORT,
      hidesDatePicker: true,
    },
    {
      // Submitted, manufacturer asked for more info / missing info (outstanding follow-up).
      id: 'followup',
      name: 'Follow-up Required',
      badgeColor: 'bg-purple-100 text-purple-700',
      filter: {
        states: [KnipperCaseState.SUBMITTED],
        outputConditions: [DECISION_IS_FOLLOW_UP],
      },
      advancedSort: SearchSortField.SEARCH_SORT_FIELD_FOLLOW_UP_REQUESTED_AT,
      advancedSortAsc: true, // oldest outstanding follow-up first
      hidesDatePicker: true,
    },
    {
      // Submitted, awaiting the manufacturer (no ack yet, no outstanding follow-up).
      id: 'submitted',
      name: 'Submitted',
      badgeColor: 'bg-blue-100 text-blue-700',
      filter: {
        states: [KnipperCaseState.SUBMITTED],
        outputConditions: [ACK_ID_NOT_EXISTS, DECISION_NOT_FOLLOW_UP],
      },
      advancedSort: SearchSortField.SEARCH_SORT_FIELD_SUBMITTED_AT,
      advancedSortAsc: true, // oldest submitted first
      hidesDatePicker: true,
    },
    {
      // Submitted, manufacturer acknowledged (ack_id present) and no outstanding follow-up.
      id: 'ready-to-complete',
      name: 'Ready to Complete',
      badgeColor: 'bg-teal-100 text-teal-700',
      filter: {
        states: [KnipperCaseState.SUBMITTED],
        outputConditions: [ACK_ID_EXISTS, DECISION_NOT_FOLLOW_UP],
      },
      advancedSort: SearchSortField.SEARCH_SORT_FIELD_ACK_RECEIVED_AT,
      advancedSortAsc: true, // oldest ack first (forgotten-to-close surfaces first)
      hidesDatePicker: true,
    },
    {
      id: 'completed',
      name: 'Completed',
      badgeColor: 'bg-green-100 text-green-700',
      filter: {
        states: [KnipperCaseState.ACKNOWLEDGED],
      },
      defaultSort: HISTORICAL_DEFAULT_SORT,
    },
    {
      id: 'cancelled',
      name: 'Cancelled',
      badgeColor: 'bg-red-50 text-red-700',
      filter: {
        states: KNIPPER_CANCELLED_STATES,
      },
      defaultSort: HISTORICAL_DEFAULT_SORT,
    },
    {
      id: 'duplicate',
      name: 'Duplicate',
      badgeColor: 'bg-yellow-100 text-yellow-700',
      filter: {
        states: [KnipperCaseState.DUPLICATE],
      },
      defaultSort: HISTORICAL_DEFAULT_SORT,
    },
    {
      id: 'other',
      name: 'Other',
      badgeColor: 'bg-slate-100 text-slate-700',
      filter: {
        states: KNIPPER_OTHER_STATES,
      },
      defaultSort: WORK_QUEUE_DEFAULT_SORT,
      hidesDatePicker: true,
    },
    {
      id: 'all',
      name: 'All',
      badgeColor: 'bg-purple-100 text-purple-700',
      filter: {},
      defaultSort: HISTORICAL_DEFAULT_SORT,
    },
  ],
};

const referralSchedulerFiltersConfig: FiltersConfig = {
  filters: [
    {
      id: 'all',
      name: 'All',
      badgeColor: 'bg-purple-100 text-purple-700',
      filter: {},
    },
    {
      id: 'pre-reachout-review',
      name: 'Pre-reachout',
      badgeColor: 'bg-purple-50 text-purple-700',
      filter: {
        states: [CaseState.PRE_REACHOUT_REVIEW],
      },
    },
    {
      id: 'ai-call-scheduled',
      name: 'AI Call Scheduled',
      badgeColor: 'bg-blue-100 text-blue-700',
      filter: {
        states: [CaseState.AI_CALL_SCHEDULED],
      },
    },
    {
      id: 'ai-call',
      name: 'AI Call',
      badgeColor: 'bg-blue-100 text-blue-700',
      filter: {
        states: [CaseState.AI_AUDIT, CaseState.AI_CALL],
      },
    },
    {
      id: 'human-call',
      name: 'Human Call',
      badgeColor: 'bg-orange-50 text-orange-700',
      filter: {
        states: HUMAN_CALL_STATES,
      },
    },
    {
      id: 'interrupted',
      name: 'Interrupted',
      badgeColor: 'bg-orange-50 text-orange-700',
      filter: {
        states: [
          CaseState.FAX_VERIFICATION,
          CaseState.PRE_PROCESSING_FAILED,
          CaseState.CASE_COMPLETION_REVIEW,
          CaseState.MISSING_INPUT,
          CaseState.CALL_REVIEW,
          CaseState.INELIGIBLE,
        ],
      },
    },
    {
      id: 'human-audit',
      name: 'Human Audit',
      badgeColor: 'bg-yellow-100 text-yellow-700',
      filter: {
        states: HUMAN_AUDIT_STATES,
      },
    },
    {
      id: 'terminated',
      name: 'Terminated',
      badgeColor: 'bg-red-50 text-red-700',
      filter: {
        states: [CaseState.TERMINATED],
      },
    },
    {
      id: 'completed',
      name: 'Completed',
      badgeColor: 'bg-green-100 text-green-700',
      filter: {
        states: [CaseState.COMPLETED],
      },
    },
  ],
};

/**
 * Refill (JNJ-31). Needs its own config rather than the default: refill shares
 * none of the generic AI-call/human-audit states the other configs filter on, so
 * the default would render tabs that can never match a refill case. "Unresolved"
 * is deliberately its own tab — a case that burned its retry budget without
 * reaching anyone is the one an operator most needs to find.
 */
const refillFiltersConfig = {
  filters: [
    {
      id: 'all',
      name: 'All',
      badgeColor: 'bg-purple-100 text-purple-700',
      filter: {},
    },
    {
      id: 'in-progress',
      name: 'AI Call',
      badgeColor: 'bg-blue-100 text-blue-700',
      filter: {
        states: [CaseState.REFILL_AI_CALL_IN_PROGRESS, CaseState.AWAIT_TRANSFER_OUTCOME],
      },
    },
    {
      id: 'call-scheduled',
      name: 'Call Scheduled',
      badgeColor: 'bg-indigo-50 text-indigo-700',
      filter: {
        states: [CaseState.CALL_SCHEDULED],
      },
    },
    {
      // Parked until a human presses Retry or Skip (JNJ-172); no timeout.
      id: 'human-audit-needed',
      name: 'Human Audit Needed',
      badgeColor: 'bg-orange-50 text-orange-700',
      filter: {
        states: [CaseState.REFILL_HUMAN_AUDIT_NEEDED],
      },
    },
    {
      // Parked until a person calls the patient and presses Mark Case as
      // Completed; no timeout. Same Waiting workflow state as Human Audit Needed,
      // so it filters on the case state.
      id: 'human-call-needed',
      name: 'Human Call Needed',
      badgeColor: 'bg-orange-50 text-orange-700',
      filter: {
        states: [CaseState.REFILL_HUMAN_CALL_NEEDED],
      },
    },
    {
      id: 'terminated',
      name: 'Terminated',
      badgeColor: 'bg-red-50 text-red-700',
      filter: {
        states: [CaseState.TERMINATED],
      },
    },
    {
      id: 'completed',
      name: 'Completed',
      badgeColor: 'bg-green-100 text-green-700',
      filter: {
        states: [CaseState.COMPLETED],
      },
    },
  ] as QuickFilterConfig[],
};

/**
 * Registry mapping WorkflowType to its specific FiltersConfig.
 * Add new workflow-specific configurations here.
 * If a workflow type is not in this registry, the default config (quickFiltersConfig) will be used.
 */
const WORKFLOW_FILTERS_REGISTRY: Partial<Record<WorkflowType, FiltersConfig>> = {
  [WorkflowType.PQC]: knipperFiltersConfig,
  [WorkflowType.RECALL_SCHEDULER]: recallSchedulerFiltersConfig,
  [WorkflowType.MEDICARE_HUMAN]: medBVFiltersConfig,
  [WorkflowType.MEDBV]: medBVFiltersConfig,
  [WorkflowType.UNIFIEDMEDBV]: unifiedMedBVFiltersConfig,
  [WorkflowType.BHO]: unifiedMedBVFiltersConfig,
  [WorkflowType.MEDBHOBV]: unifiedMedBVFiltersConfig,
  [WorkflowType.PBM]: pharmBVFiltersConfig,
  [WorkflowType.REFERRAL_SCHEDULER]: referralSchedulerFiltersConfig,
  [WorkflowType.TRANSPORTATION]: pharmBVFiltersConfig,
  [WorkflowType.PA_STATUS_PBM]: unifiedMedBVFiltersConfig,
  [WorkflowType.PA_STATUS_MEDBV]: unifiedMedBVFiltersConfig,
  [WorkflowType.REFILL]: refillFiltersConfig,
};

/**
 * Gets the filters config for a given workflow type.
 * Returns the workflow-specific config if one exists, otherwise falls back to
 * the default `quickFiltersConfig`.
 */
export function getFiltersConfigForWorkflowType(
  workflowType: WorkflowType | undefined,
): FiltersConfig {
  if (workflowType && workflowType in WORKFLOW_FILTERS_REGISTRY) {
    return WORKFLOW_FILTERS_REGISTRY[workflowType]!;
  }
  return quickFiltersConfig;
}
